/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed} from 'mobx';
import moment from 'moment';
import {getLangFromUrlPathname} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {RootStore} from '../root-store';
import {RecordModel} from '../jvlns/record-model';

import Javelin, {
    Gender,
    LocalDate,
    RegionAbbrevId,
    javelinFactory,
    Relationship,
    RelationshipType,
    ZonedDateTime,
    CityInstance,
    RelationshipOpt,
} from '@canimmunize/javelinscript';

import {translateFn, config, loggableObs} from '@canimmunize/core-imports';

const fnCreateRec = `CreateNewRecordStore#createRecordFromData ::`;

/******************************************** DEFAULTS ********************************************/
const firstNameDefault = ``;
const lastNameDefault = ``;
const genderDefault = Gender.BOTH; // 0 | 1 | 2
const birthdateDefault = moment();

/********************************************* EXPORT *********************************************/
/**
 * Data store for current contents of Create New Record modal
 */
export class CreateNewRecordStore {
    public static Instance: CreateNewRecordStore | null = null;

    /**
     * Which screen are we currently on?
     */
    @observable
    public currentScreen: 0 | 1 | 2 = 0;

    /**
     * Is the exit prompt showing?
     */
    @observable
    public exitPromptShowing = false;

    /**
     * Which field in the modal is currently selected?
     */
    @observable
    public selectedInput = `firstName`;

    /*
     * CURRENT FORM CONTENT / DATA
     */

    @observable
    public firstName: string = firstNameDefault;

    @observable
    public lastName: string = lastNameDefault;

    @observable
    public birthdate: moment.Moment = birthdateDefault;

    @observable
    public gender: number = genderDefault; // type: 0 | 1 | 2

    @observable
    public relationship: RelationshipOpt = Relationship.SELF; // type: -1 | 0 | 1 | 2 | 3

    @observable
    public city: CityInstance | null = null;

    @observable
    public province: RegionAbbrevId = `` as any;

    @observable
    public provinceLocal = ``;

    @observable
    public consent = false;

    /**
     * Returns true if data has changed from its initial values
     */
    @computed
    public get hasChanged(): boolean {
        return (
            this.firstName !== firstNameDefault ||
            this.lastName !== lastNameDefault ||
            this.gender !== genderDefault ||
            this.birthdate !== birthdateDefault
        );
    }

    /*
     * SETTERS
     */

    @action
    public setCurrentScreen = (val: 0 | 1 | 2) => (this.currentScreen = val);

    @action
    public setExitPromptShowing = (showing: boolean) => (this.exitPromptShowing = showing);

    @action
    public setSelectedInput = (val: string) => (this.selectedInput = val);

    @action
    public setFirstName = (val: string) => (this.firstName = val);

    @action
    public setLastName = (val: string) => (this.lastName = val);

    @action
    public setBirthdate = (val: moment.Moment) => (this.birthdate = val);

    @action
    public setRelationship = (val: RelationshipOpt) => (this.relationship = val);

    /**
     * Set gender by numeric identifier e.g. setGender(1)
     *   0 = MALE
     *   1 = FEMALE
     */
    @action
    public setGender = (val: number) => (this.gender = val);

    @action
    public setCity = (val: CityInstance) => (this.city = val);

    @action
    public setProvince = (val: RegionAbbrevId) => (this.province = val);

    @action
    public setProvinceLocal = (val: string) => (this.provinceLocal = val);

    @action
    public setConsent = (val: boolean) => (this.consent = val);

    @action
    public showConfirmExitPrompt = () => (this.exitPromptShowing = true);

    @action
    public hideConfirmExitPrompt = () => (this.exitPromptShowing = false);

    /*
     * MISC ACTIONS: record model creation, local data wiping
     */

    /**
     * Create RecordModel from given data, then store it in rootModel
     */
    @action
    public createRecordFromData = async (): Promise<RecordModel> => {
        // TODO determine if the JVLNS rebuild below is necessary
        // Build or get JVLNS (in case it's not yet initialized)
        const javelin = await javelinFactory({
            clientVersion: this.rootStore.envVarStore.clientVersion,
            translateFn,
            currentLanguage: getLangFromUrlPathname(),
        });

        // Build JVLNS Record instance
        const rec = javelin.buildRecord(
            this.firstName,
            this.lastName,
            this.gender,
            new LocalDate(this.birthdate),
            this.relationship,
            ``,
            this.city ? this.city.cityId : ``,
            ``,
            this.consent ? new ZonedDateTime(moment()) : null, // TODO real consent
            this.province
        );

        // Build RecordModel from JVLNS instance
        const recordModel = this.rootStore.recordModelsStore.addRecordModel(rec);

        // Clear modal flow, then return new RecordModel
        this.clear();
        return recordModel;
    };

    /**
     * Erase all temporary data stored in the create new record wizard
     */
    @action
    public clear = () => {
        // Screen 1
        this.setFirstName(firstNameDefault);
        this.setLastName(lastNameDefault);
        this.setBirthdate(birthdateDefault);
        this.setGender(genderDefault);

        // Screen 2
        this.setRelationship(Relationship.SELF);

        // Screen 3
        this.setCity(null);
        this.setProvince(`` as any);
        this.setProvinceLocal(``);
        this.setConsent(false);

        // Reset current sceren
        this.setCurrentScreen(0);
        this.setExitPromptShowing(false);
        this.setSelectedInput(`firstName`);
    };

    /**
     * CreateNewRecordStore singleton factory method
     */
    public static new = (rootStore: RootStore): CreateNewRecordStore => {
        if (!CreateNewRecordStore.Instance)
            CreateNewRecordStore.Instance = new CreateNewRecordStore(rootStore);
        return CreateNewRecordStore.Instance;
    };

    private constructor(public rootStore: RootStore) {}
}
